import React, { useState } from "react";
import { observer } from "mobx-react";
import copilotStore from "Stores/copilotStore";
import BotGlyph from "Pages/Dashboard/agents/BotGlyph";

// Icon per "what the chat produced". Material Icons Outlined v8 names only.
const KIND_ICON = { list: "list", sequence: "send", research: "description", chat: "chat_bubble_outline" };

export const ChatKindIcon = ({ kind, size = "md" }) => (
  <span className={`cpChatKind cpChatKind--${kind || "chat"} cpChatKind--${size}`}>
    {kind === "agent" ? <BotGlyph size={size === "sm" ? 12 : 14} /> : <i className="material-icons-outlined">{KIND_ICON[kind] || KIND_ICON.chat}</i>}
  </span>
);

const dayStart = d => { const x = new Date(d); x.setHours(0, 0, 0, 0); return x.getTime(); };
const groupOf = ts => {
  const diff = Math.round((dayStart(Date.now()) - dayStart(ts || 0)) / 86400000);
  if (diff <= 0) return "Today";
  if (diff === 1) return "Yesterday";
  if (diff < 7) return "Last 7 days";
  return "Earlier";
};
const whenOf = ts => {
  if (!ts) return "";
  const d = new Date(ts);
  return groupOf(ts) === "Today" || groupOf(ts) === "Yesterday"
    ? d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
    : d.toLocaleDateString([], { day: "numeric", month: "short" });
};

export const chatTitle = c => (c && c.title) || "New conversation";

/**
 * Chats grouped by day, each with what it produced. Used by the header switcher
 * (with rename/delete) and by the recents list under the empty home composer.
 */
const ChatList = observer(({ items, activeIndex = -1, onOpen, onDeleted, editable = false }) => {
  const [editingId, setEditingId] = useState(null);
  const [editValue, setEditValue] = useState("");
  let lastGroup = "";
  return (
    <div className="cpChatList" role="listbox" aria-label="Chats">
      {items.map((c, i) => {
        const g = groupOf(c.lastActivityTs || c.createTs);
        const head = g !== lastGroup ? <div className="cpChatList__group" key={`g-${g}`}>{g}</div> : null;
        lastGroup = g;
        const active = c.id === copilotStore.conversationId;
        const editing = editingId === c.id;
        return (
          <React.Fragment key={c.id}>
            {head}
            <div
              role="option"
              aria-selected={i === activeIndex}
              tabIndex={-1}
              className={`cpChatList__row${active ? " is-current" : ""}${i === activeIndex ? " is-focus" : ""}`}
              onMouseDown={e => e.preventDefault()}
              onClick={() => !editing && onOpen(c)}
            >
              <ChatKindIcon kind={c.kind} />
              <span className="cpChatList__text">
                {editing ? (
                  <input
                    className="cpChatList__input"
                    value={editValue}
                    autoFocus
                    aria-label="Chat name"
                    onClick={e => e.stopPropagation()}
                    onMouseDown={e => e.stopPropagation()} // let the caret/selection work; the row prevents default
                    onChange={e => setEditValue(e.target.value)}
                    onBlur={() => { copilotStore.renameConversation(c.id, editValue); setEditingId(null); }}
                    onKeyDown={e => {
                      e.stopPropagation();
                      if (e.key === "Enter") e.target.blur();
                      if (e.key === "Escape") setEditingId(null);
                    }}
                  />
                ) : (
                  <span className="cpChatList__title">{chatTitle(c)}</span>
                )}
                {c.summary && !editing && <span className="cpChatList__summary">{c.summary}</span>}
              </span>
              <span className="cpChatList__when">{whenOf(c.lastActivityTs || c.createTs)}</span>
              {editable && !editing && (
                <span className="cpChatList__actions">
                  <button type="button" aria-label="Rename chat" onClick={e => { e.stopPropagation(); setEditingId(c.id); setEditValue(chatTitle(c)); }}>
                    <i className="material-icons-outlined">edit</i>
                  </button>
                  <button type="button" aria-label="Delete chat" onClick={e => {
                    e.stopPropagation();
                    const wasActive = c.id === copilotStore.conversationId;
                    copilotStore.deleteConversation(c.id);
                    if (onDeleted) onDeleted(wasActive);
                  }}>
                    <i className="material-icons-outlined">delete_outline</i>
                  </button>
                </span>
              )}
            </div>
          </React.Fragment>
        );
      })}
    </div>
  );
});

export default ChatList;
